import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Navigate, Outlet, useNavigate } from 'react-router-dom'
import { IndentifiedTranslatorContext } from 'context/IdentifiedTranslatorContext'
import { io } from 'socket.io-client'
import getSocketUrl from 'helpers/socketUrl'
import { AuthContext } from './AuthContext'

const roomAvailableEventName = 'roomAvailable'
const callFinishedEventName = 'callFinished'

export const TranslatorDataContext = createContext({
  ws: {},
  availableRoom: {},
  setAvailableRoom: () => {},
})

const TranslatorDataProvider = ({ children }) => {
  const { user } = useContext(AuthContext)
  const { translator } = useContext(IndentifiedTranslatorContext)
  const navigate = useNavigate()

  const [ws, setWs] = useState(null)
  const [availableRoom, setAvailableRoom] = useState(null)

  const fetchOngoingCall = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_DOMAIN}/translators/calls`, {
        credentials: 'include',
        headers: {
          authorization: `Bearer ${user.accessToken}`,
        },
      })
      const json = await res.json()
      if (res.ok && json?.data?.roomId) {
        setAvailableRoom(json.data)
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log({ translatorCallSyncErr: err?.message || err })
    }
  }, [user?.accessToken])

  const identified = !!translator?.firstName
    && !!translator?.lastName
    && !!translator?.speciality
  useEffect(() => {
    if (!identified) return
    const ws = io(getSocketUrl(), {
      auth: {
        token: `Bearer ${user.accessToken}`,
      },
      query: { translatorData: JSON.stringify(translator) },
      withCredentials: true,
      transports: ['websocket', 'polling'],
    })
    const handleRoomAvailable = (data) => {
      if (data) {
        new Audio('/notification.mp3').play()
      }
      setAvailableRoom(data)
    }
    const handleCallFinished = () => {
      setAvailableRoom(null)
      window.setTimeout(() => {
        navigate('/translators/callFinished', { replace: true })
      }, 200)
    }

    ws.on(roomAvailableEventName, handleRoomAvailable)
    ws.on(callFinishedEventName, handleCallFinished)
    ws.on('connect', fetchOngoingCall)
    setWs(ws)
    fetchOngoingCall()
    window.addEventListener('focus', fetchOngoingCall)
    return () => {
      window.removeEventListener('focus', fetchOngoingCall)
      ws.off(roomAvailableEventName, handleRoomAvailable)
      ws.off(callFinishedEventName, handleCallFinished)
      ws.off('connect', fetchOngoingCall)
      ws.close()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, fetchOngoingCall, navigate])

  const contextValue = useMemo(
    () => ({ ws, availableRoom, setAvailableRoom }),
    [ws, availableRoom],
  )

  if (!identified) return <Navigate to='/translators/identify' />

  return (
    <TranslatorDataContext.Provider value={contextValue}>
      <Outlet />
    </TranslatorDataContext.Provider>
  )
}

export default TranslatorDataProvider
